import { createContext, useContext, useRef, useState, type ReactNode } from 'react';

import { Capacitor } from '@capacitor/core';

export type DeviceClass = 'phone' | 'tablet' | 'desktop';

type Device = { deviceClass: DeviceClass; isNative: boolean };

export function classifyDevice(shortest: number, isNative: boolean): DeviceClass {
  if (shortest < 600) return 'phone';
  return isNative ? 'tablet' : 'desktop';
}

function launchDevice(): Device {
  const isNative = Capacitor.isNativePlatform();
  return {
    isNative,
    deviceClass: classifyDevice(
      Math.min(window.screen.width, window.screen.height),
      isNative
    ),
  };
}

const DeviceClassContext = createContext<Device | null>(null);

export function DeviceClassProvider({ children }: { children: ReactNode }) {
  // A foldable unfolding (or a rotation/resize) needs an app relaunch to change
  // class; do not subscribe to resize or orientation events here.
  const [device] = useState(launchDevice);
  return (
    <DeviceClassContext.Provider value={device}>
      {children}
    </DeviceClassContext.Provider>
  );
}

export function useDeviceClass() {
  const device = useContext(DeviceClassContext);
  const fallback = useRef<Device | null>(null);
  if (device) return device;

  // Only isolated consumers without the App root classify here. The app's
  // provider computes once; native cannot silently fall back to web behavior.
  fallback.current ??= launchDevice();
  return fallback.current;
}
